import { faCircleExclamation, faLockKeyhole, faUsers } from '@fortawesome/pro-regular-svg-icons';
import Button from '@sharedComponents/Button';
import FlexBox from '@sharedComponents/FlexBox';
import InformationMessage from '@sharedComponents/InformationMessage';
import Typography from '@sharedComponents/Typography';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RESTRICTED, scopeChangeConsequences } from '../accessRules';

import * as style from './style.module.css';

/**
 * Body of the modal confirming a scope change towards `target` ('PUBLIC' or 'RESTRICTED'): an
 * introduction, the consequences that apply to the current `overrides` rows, and a warning. The focus
 * starts on "Cancel"; the modal answers only once, as it stays mounted, faded out, once closed.
 */
const ScopeChangeModal = ({ target, overrides = [], onCancel = () => {}, onConfirm = () => {} }) => {
    const { t } = useTranslation('bo');
    const consequences = scopeChangeConsequences(overrides, target);
    const toRestricted = target === RESTRICTED;
    const cancelRef = useRef(null);
    const [answered, setAnswered] = useState(false);

    useEffect(() => {
        cancelRef.current?.focus();
    }, []);

    const answer = (callback) => () => {
        if (answered) return;
        setAnswered(true);
        callback();
    };

    // Each sentence with the condition that shows it, in display order.
    const sentences = toRestricted
        ? [
              [
                  consequences.textsKeptWithoutAccess,
                  t(
                      'The clients and platforms with an overridden title or description keep them, but have no access until you authorize them.',
                  ),
              ],
              [
                  consequences.hiddenRemoved,
                  t(
                      'The clients and platforms for which the collection was hidden, without an overridden title or description, are removed from the list.',
                  ),
              ],
              [
                  consequences.listedRemoved,
                  t('The clients and platforms listed without an overridden title or description are removed from the list.'),
              ],
              [
                  consequences.textsKeptWithoutAccess,
                  t(
                      'Authorizing a client will give access to its platforms and to those of its sub-clients, except those that have their own row: authorize them separately.',
                  ),
              ],
          ]
        : [
              [
                  consequences.authorizedTextsKept,
                  t('The authorized clients and platforms with an overridden title or description keep them.'),
              ],
              [
                  consequences.textsNowVisible,
                  t(
                      'The clients and platforms that had an overridden title or description without access will now see the collection, with their texts.',
                  ),
              ],
              [
                  consequences.authorizedListedWithoutOverride,
                  t(
                      'The authorized clients and platforms without an overridden title or description stay listed, without override, and can be removed from the list.',
                  ),
              ],
              [
                  overrides.length > 0,
                  t(
                      'Nothing is hidden after the change: you can hide the collection from a client, a sub-client or a platform in the clients table.',
                  ),
              ],
          ];
    const shown = sentences.filter(([applies]) => applies).map(([, sentence]) => sentence);

    return (
        <FlexBox flexDirection="column" gap="s">
            <Typography>
                {toRestricted
                    ? t(
                          'Only the clients and platforms you authorize will see the collection. Right after this change, none is authorized.',
                      )
                    : t('All clients will see the collection, according to their activated packs.')}
            </Typography>
            {shown.length > 0 && (
                <FlexBox role="list" flexDirection="column" gap="xs">
                    {shown.map((sentence) => (
                        <FlexBox key={sentence} role="listitem" gap="xs" alignItems="baseline" className={style.item}>
                            <Typography fontSize="s" color="neutral-secondary">
                                {sentence}
                            </Typography>
                        </FlexBox>
                    ))}
                </FlexBox>
            )}
            <InformationMessage
                status="warning"
                icon={faCircleExclamation}
                text={
                    toRestricted
                        ? t('As long as no client is authorized, the collection is visible to no one.')
                        : t('The collection becomes visible to all clients that have the relevant packs.')
                }
            />
            <FlexBox justifyContent="flex-end" gap="s" flexWrap="wrap">
                <Button ref={cancelRef} outline label={t('Cancel')} action={answer(onCancel)} />
                <Button
                    icon={toRestricted ? faLockKeyhole : faUsers}
                    label={toRestricted ? t('Make it restricted') : t('Make it public')}
                    action={answer(onConfirm)}
                />
            </FlexBox>
        </FlexBox>
    );
};

export default ScopeChangeModal;
